import { Location } from '@angular/common';
import { Observable, Subject, ReplaySubject } from 'rxjs';
import { SapiEntity } from './../../../services/sapi.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgForm } from '@angular/forms';
import { AbstractDetailsComponent } from '../../shared/abstract-details.component';
import { UploadCredentials } from '../../../interfaces/publication';
import { DialogDefinition, NoticeDefinition } from 'app/services/overlay.service';
import { ClipboardService } from 'ngx-clipboard';

@Component({
	selector: 'upload-credentials-details',
	templateUrl: './upload-credentials-details.component.html',
	styleUrls: ['./upload-credentials-details.component.scss']
})

export class UploadCredentialsDetailsComponent extends AbstractDetailsComponent implements OnInit {
	@ViewChild('formRef', { static: true }) form: NgForm;
	initialLabel: string;
	newSecretKey = false;
	uploadCredentials: UploadCredentials;
	activeTab: string;
	loading = false;
	id: string;
	isNew: boolean;
	putRight: boolean;
	entity: any;

	uploadSubject: ReplaySubject<UploadCredentials>;

	constructor(
		protected clipboardService: ClipboardService,
		protected route: ActivatedRoute,
		protected location: Location) {

		super(route, location);

		this.authenticationService.hasPermission('put').subscribe(hasRight => this.putRight = hasRight);
		this.uploadSubject = new ReplaySubject<UploadCredentials>(1);

		this.entityConfiguration = {
			name: 'uploadcredentials',
			parentPath: '/publication/upload-credentials',
			basePath: '/publication/upload-credentials/',
			defaults: {
				label: '',
				status: 'active'
			} as UploadCredentials
		};
	}

	ngOnInit() {
		super.ngOnInit();
		this.uploadCredentials = { ...this.entity } as UploadCredentials;
	}

	delete(dialogContents?: string): Subject<void> {
		dialogContents = this.translate.instant('DELETE_ENTITIES_CONFIRM', {
			entities: this.translate.instant('UPLOAD_CREDENTIALS').toLowerCase()
		});
		return super.delete(dialogContents);
	}

	save(force: boolean = false): Observable<void> {
		if (this.isNew) {
			const saveSubject = super.save(force, null, false, true);
			const dialog: DialogDefinition = {
				icon: 'warning',
				title: this.translate.instant('NEW_SECRET_KEY'),
				contents: this.translate.instant('RECEIVE_KEY_SURE'),
				onConfirm: () => {
					this.loading = true;
					this.overlayService.setLoading(true);
					const payload = { type: 's3Credentials', label: this.entity.label, status: this.entity.status };

					this.sapiService.putUrl('/uploadcredentials', payload).subscribe((response) => {
						if (response.awsSecretAccessKey) {
							const notice: NoticeDefinition = {
								icon: 'info-circle',
								title: this.translate.instant('NEW_SECRET_KEY'),
								type: 'info',
								showCopy: true,
								contents: this.translate.instant('RECEIVED_SECRET_KEY', {
									secret: response.awsSecretAccessKey.bold(),
								}),
								onConfirm: () => {
									if (this.isNew) {
										this.isNew = false;
									}
									this.entity = response;
									this.form.form.markAsPristine(); // already saved so no saveSubject.subscribe() needed
								},
								onCopy: () => {
									const msg = this.translate.instant('COPIED_SECRET_TO_CLIPBOARD');
									this.clipboardService.copyFromContent(response.awsSecretAccessKey);
									this.overlayService.createToast(this.translate.instant(msg), 'copy');
								}
							};
							this.overlayService.createNotice(notice);
						} else {
							this.overlayService.createToast(this.translate.instant('ERROR'), 'warning');
						}
						this.overlayService.setLoading(false);
					},
					(err) => {
						this.overlayService.createToast(this.translate.instant('ERROR'), 'warning');
						this.loading = false;
						this.overlayService.setLoading(false);
					});
				},
				onCancel: () => {
					this.cancel();
					this.loading = false;
					this.overlayService.setLoading(false);
					return;
				}
			};
			this.overlayService.createDialog(dialog);
			this.loading = false;
			this.overlayService.setLoading(false);
			return saveSubject;
		} else {
			this.loading = true;
			this.overlayService.setLoading(true);
			const payload =  { id: this.entity.id, label: this.entity.label, status: this.entity.status };
			this.sapiService.putUrl('/uploadcredentials', payload).subscribe(() => {
				this.overlayService.createToast(this.translate.instant('ENTITY_SAVED', {
					entity: this.translate.instant('UPLOAD_CREDENTIALS'),
					id: ''
				}), 'save');
				this.cancel();
				this.loading = false;
				this.overlayService.setLoading(false);
			},
			(err) => {
				this.overlayService.createToast(this.translate.instant('ERROR'), 'warning');
				this.loading = false;
				this.overlayService.setLoading(false);
			});
		}
	}

	load(id: string): Observable<SapiEntity> {
		const loadObservable = super.load(id);
		loadObservable.subscribe(() => {
			if (this.entity) {
				this.uploadCredentials = this.entity as UploadCredentials;
				this.initialLabel = this.uploadCredentials.label;
			}
		});

		return loadObservable;
	}
}
